<template>
  <div
    v-if="$parent.inMeeting"
    id="bottomControls"
    :class="{ 'control-is-mobile': $parent.isMobile() }"
    class="fixed-bottom meeting-controls d-flex">
    <volume-indicator
      v-if="$parent.inMeeting"
      ref="miceVolume"
      :display="false"
      :media="$parent.localAudioStream[0].media"
      :mike="mic"
    />
    <div
      id="layout"
      class="mr-auto layoutLauncher control"
      tabindex="0"
      @click="closeToolTip">
      <grid-icon
        v-if="selectedVideoLayout === 5"
        :size="32"/>
      <grid-icon
        v-if="selectedVideoLayout === 0"
        :size="32"/>
      <gesture-tap-icon
        v-if="selectedVideoLayout === 1"
        :size="32" />
      <account-voice-icon
        v-if="selectedVideoLayout === 2"
        :size="32" />
      <no-video-icon
        v-if="selectedVideoLayout === 3"
        :size="32" />
      <limited-icon
        v-if="selectedVideoLayout === 4"
        :size="32" />
      <b-tooltip
        v-if="!$parent.isMobile()"
        target="layout"
        triggers="hover">{{ $t('MEETING.CONTROLS_DISPLAY_LAYOUT') }}</b-tooltip>
    </div>
    <div class="m-auto">
      <span
        v-if="!canSeeAudioVideoCtrls && $parent.meetingInfo.webinar && !$parent.$refs.sideBar.$refs.sidePart.amImoderator"
        id="raiseHand"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control "
        @click="launchRaiseHand(!isRaisedHand)">
        <raise-hand-icon
          :size="32"
          :fill-color="isRaisedHand ? '#65CA30' : '#F8F8F8'"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="raiseHand"
          triggers="hover">{{ !isRaisedHand ? $t('MEETING.RAISE_HAND') : $t('MEETING.LOWER_HAND') }}</b-tooltip>
      </span>
      <span
        v-if="canSeeAudioVideoCtrls && $parent.meetingInfo.webinar"
        id="returnAudience"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control "
        @click="$parent.$refs.sideBar.$refs.sidePart.becomeAudience()">
        <become-speaker-icon
          :rotate="180"
          :size="32"
          class="mr-2"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="returnAudience"
          triggers="hover">{{ $t('MEETING.RETURN_AUDIENCE') }}</b-tooltip>
      </span>
      <span
        v-if="!canSeeAudioVideoCtrls && $parent.meetingInfo.webinar && $parent.$refs.sideBar.$refs.sidePart.amImoderator"
        id="becomeSpeaker"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control "
        @click="$parent.$refs.sideBar.$refs.sidePart.becomeSpeaker()">
        <become-speaker-icon
          :size="32"
          class="mr-2"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="becomeSpeaker"
          triggers="hover">{{ $t('MEETING.BECOME_SPEAKER') }}</b-tooltip>
      </span>
      <span
        v-if="canSeeAudioVideoCtrls"
        id="videControl"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control "
        @click="camera = !camera">
        <video-icon
          v-if="camera"
          id="video"
          :size="32"/>
        <video-off-icon
          v-else
          id="video-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="videControl"
          triggers="hover">{{ toggleText(camera,'_VIDEO') }}</b-tooltip>
      </span>
      <span
        v-if="canSeeAudioVideoCtrls"
        id="micControl"
        class="d-inline-block control middle-controls"
        @click="mic = !mic">
        <microphone-icon
          v-if="mic"
          id="mic"
          :size="32"/>
        <microphone-off-icon
          v-else
          id="mic-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="micControl"
          triggers="hover">{{ toggleText(mic,'_ME', 'CONFIGURE.MUTE', 'CONFIGURE.UNMUTE').toString() }}</b-tooltip>
      </span>
      <span
        id="speakerControl"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control"
        @click="speaker = !speaker">
        <volume-high-icon
          v-if="speaker"
          id="speaker"
          :size="32"/>
        <volume-off-icon
          v-else
          id="speaker-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="speakerControl"
          triggers="hover">{{ toggleText(speaker,'_AUDIO') }}</b-tooltip>
      </span>
      <span
        v-if="!$parent.isMobile() && $parent.isSupportedShare() && canSeeAudioVideoCtrls"
        v-show="desktopControls"
        id="desktopControl"
        class="d-md-inline-block control middle-controls d-none"
        @click="onDesktopChange()">
        <screen-share-icon
          v-if="!desktop"
          id="desktop"
          :size="32"/>
        <screen-share-stop-icon
          v-else
          id="desktop-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="desktopControl"
          triggers="hover">{{ toggleText(desktop,'_SHARESCREEN') }}</b-tooltip>
      </span>
      <span
        id="inMeetingSettings"
        :class="{'middle-controls': !$parent.isMobile() }"
        tabindex="0"
        class="control d-inline-block"
        @click="closeToolTip">
        <dots-horizontal-icon
          v-b-tooltip.hover
          :size="32"/>
      </span>
      <b-tooltip
        v-if="!$parent.isMobile()"
        target="inMeetingSettings"
        triggers="hover">
        {{ $t('MEETING.OPEN_MORE_MEETING_CONTROLS') }}
      </b-tooltip>
    </div>
    <span class="control ml-auto danger-hover">
      <exit-to-app-icon
        v-if="$parent.isMobile()"
        id="quit"
        :size="32"
        tabindex="0"
        @click="exitMeeting()"/>
      <b-btn
        v-if="!$parent.isMobile()"
        style="margin-top:5px;"
        variant="red"
        @click="launchLeave()">
        {{ $t('MEETING.LEAVE') }}
      </b-btn>
    </span>
    <b-tooltip
      v-if="!$parent.isMobile()"
      target="quit"
      triggers="hover">
      {{ $t('MEETING.TITLELEAVE') }}
    </b-tooltip>

    <b-popover
      ref="meetingSettings"
      target="inMeetingSettings"
      triggers="focus"
      placement="top">
      <ul class="list-unstyled setting-list">
        <li
          class="d-flex position-relative"
          @click="toggleSystemMessages()">
          <alert-icon
            :size="20"
            class="mr-2"/>
          {{ $parent.$refs.sideBar.system ? $t('MEETING.HIDE_SYS_MSGS') : $t('MEETING.SHOW_SYS_MSGS') }}
        </li>
        <li
          v-if="!$parent.$refs.sideBar.$refs.sidePart.amImoderator"
          class="d-flex position-relative"
          @click="launchMakeModerator()">
          <moderator-key-icon
            :size="20"
            class="mr-2"/>
          {{ $t('MEETING.ENTER_MOD_KEY') }}
        </li>
        <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.amImoderator"
          class="d-flex position-relative"
          @click="muteUsersAudio()">
          <microphone-off-icon
            :size="20"
            class="mr-2"/>
          {{ $t('MEETING.MUTE_AUDIO_ALL') }}
        </li>
        <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.amImoderator"
          class="d-flex position-relative"
          @click="muteUsersVideo()">
          <video-off-icon
            :size="20"
            class="mr-2"/>
          {{ $t('MEETING.MUTE_VIDEO_ALL') }}
        </li>
        <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.amImoderator"
          class="d-flex position-relative"
          @click="toogleLock()">
          <lock-outline-icon
            :size="20"
            class="mr-2"/>
          {{ isMeetingLocked ? $t('MEETING.UNLOCK_MEETING') : $t('MEETING.LOCK_MEETING') }}
        </li>
        <!-- <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.amImoderator"
          class="d-flex position-relative"
          @click="$parent.makeWaitingRoom(!$parent.meetingInfo.waitingRoom); $refs.meetingSettings.$emit('close')">
          <waiting-room-icon
            :size="20"
            class="mr-2"/>
          {{ $parent.meetingInfo.waitingRoom ? $t('MEETING.DISABLE_WAIT_ROOM') : $t('MEETING.ENABLE_WAIT_ROOM') }}
        </li> -->
        <hr>
        <li
          class="d-flex position-relative"
          @click="launchSettings('video')">
          <video-icon
            :size="20"
            class="mr-2"/> {{ $t('MEETING.CONTROLS_VIDEO_PREF') }}
        </li>
        <li
          class="d-flex position-relative"
          @click="launchSettings('audio')">
          <microphone-icon
            :size="20"
            class="mr-2 d-flex"/> {{ $t('MEETING.CONTROLS_AUDIO_PREF') }}
        </li>
      </ul>
    </b-popover>
    <b-popover
      ref="layoutRef"
      custom-class="gridOptions"
      target="layout"
      triggers="focus"
      placement="topright">
      <ul class="list-unstyled setting-list">
        <li
          class="d-flex position-relative"
          @click="changeVideoLayoutOption(5)">
          <span>
            <grid-icon
              :size="20"
              class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_GRID_VIDEOEVERYONE') }}</span>
        </li>
        <li
          class="d-flex position-relative"
          @click="changeVideoLayoutOption(0)">
          <span>
            <grid-icon
              :size="20"
              class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_GRID_VIDEOONLY') }}</span>
        </li>
        <li
          class="d-flex position-relative"
          @click="changeVideoLayoutOption(2)">
          <span>
            <account-voice-icon
              :size="20"
              class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_SPEAKER') }}</span>
        </li>
        <li
          v-if="!$parent.isMobile()"
          class="d-md-flex position-relative d-none"
          @click="changeVideoLayoutOption(1)">
          <span>
            <gesture-tap-icon
              :size="20"
              class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_CLICKTOSELECT') }}</span>
        </li>
        <hr>
        <li
          class="d-md-flex position-relative"
          @click="changeVideoLayoutOption(4)">
          <span>
            <limited-icon
              :size="20"
              class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_LIMITED') }}</span>
        </li>
        <li @click="changeVideoLayoutOption(3)">
          <no-video-icon
            :size="20"
            class="mr-2"/>{{ $t('MEETING.CONTROLS_LAYOUT_STOPVIDEO') }}
        </li>
      </ul>
    </b-popover>
    <b-popover
      v-if="canSeeAssignLeaveMenu()"
      ref="quitMenu"
      target="quit"
      triggers="focus"
      placement="top">
      <ul class="list-unstyled setting-list">
        <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.areModerators.length < 2 && $parent.$refs.sideBar.$refs.sidePart.participantList.length > 1"
          class="d-flex position-relative quit-title">
          {{ $t('MEETING.MENU_ASSIGN_MODERATOR') }}
        </li>
        <div
        v-if="$parent.meetingInfo.waitingRoom">
          <li
            v-for="(person, index) in $parent.$refs.sideBar.$refs.sidePart.inMeeting.filter( p => { return (p.presence && p.presence.isModerator == false && p.isSelf != true) })"
            :key="index"
            class="d-flex position-relative"
            @click="assignModerator(person.id)">
            {{ person.firstName }} {{ person.lastName }}
            <check-icon
              v-if="moderatorIds.findIndex(e=>e==person.id) >=0"
              :size="20"
              class="ml-auto"/>
          </li>
        </div>
        <div
        v-if="$parent.meetingInfo.webinar && $parent.$refs.sideBar.$refs.sidePart.areModerators.length < 2">
          <li
            v-for="(person, index) in $parent.$refs.sideBar.$refs.sidePart.participantList.filter( p => { return (p.presence && p.presence.isModerator == false && p.isSelf != true) })"
            :key="index"
            class="d-flex position-relative"
            @click="assignModerator(person.id)">
            {{ person.firstName }} {{ person.lastName }}
            <check-icon
              v-if="moderatorIds.findIndex(e=>e==person.id) >=0"
              :size="20"
              class="ml-auto"/>
          </li>
        </div>
        <li
          v-if="$parent.$refs.sideBar.$refs.sidePart.areModerators.length < 2 && $parent.$refs.sideBar.$refs.sidePart.participantList.length > 1"
          class="d-flex position-relative quit-leave"
          @click="assignLeave">
          {{ $t('MEETING.MENU_ASSIGN_LEAVE') }}
        </li>
        <li
          v-else
          class="d-flex position-relative quit-leave"
          @click="$parent.exitMeeting()">
          {{ $t('MEETING.LEAVE') }}
        </li>
        <li
          v-if="$parent.meetingInfo.webinar"
          style="margin-top:5px"
          class="d-flex position-relative end-webinar"
          @click="toogleWebinar(false)">
          {{ $t('MEETING.END_WEBINAR') }}
        </li>
      </ul>
    </b-popover>
  </div>
</template>


<script>
import VolumeIndicator from './VolumeIndicator'
import { ref, watch, computed } from "@vue/composition-api"
import NoVideoIcon from "../icons/NoVideo"
import ExitToAppIcon from "icons/ExitToApp"
import MicrophoneIcon from "icons/Microphone"
import MicrophoneOffIcon from "icons/MicrophoneOff"
import VideoIcon from "icons/Video"
import VideoOffIcon from "icons/VideoOff"
import VolumeHighIcon from "icons/VolumeHigh"
import VolumeOffIcon from "icons/VolumeOff"
import ScreenShareIcon from "../icons/ScreenShare"
import ScreenShareStopIcon from "../icons/ScreenShareStop"
import DotsHorizontalIcon from "icons/DotsHorizontal"
import ChevronDownIcon from "icons/ChevronDown"
import CogIcon from "icons/Cog"
import AccountVoiceIcon from "icons/AccountVoice"
import GestureTapIcon from "icons/GestureTap"
import GridIcon from "../icons/Grid"
import AlertIcon from "icons/Alert"
import LimitedIcon from "../icons/Limited"
import WaitingRoomIcon from "../icons/WaitingRoom"
import CheckIcon from "icons/Check"
import ModeratorKeyIcon from '../icons/ModeratorKey'
import LockOutlineIcon from "icons/LockOutline"
import RaiseHandIcon from "../icons/RaiseHand"
import BecomeSpeakerIcon from "../icons/BecomeSpeaker"
import { gaEvent } from '../functions/gtag'

export default {
  name: 'MeetingControls',
  components: {
    'volume-indicator': VolumeIndicator,
    'no-video-icon': NoVideoIcon,
    'exit-to-app-icon': ExitToAppIcon,
    'microphone-icon': MicrophoneIcon,
    'microphone-off-icon': MicrophoneOffIcon,
    'video-icon': VideoIcon,
    'video-off-icon': VideoOffIcon,
    'volume-high-icon': VolumeHighIcon,
    'volume-off-icon': VolumeOffIcon,
    'screen-share-icon': ScreenShareIcon,
    'screen-share-stop-icon': ScreenShareStopIcon,
    'dots-horizontal-icon': DotsHorizontalIcon,
    'chevron-down-icon': ChevronDownIcon,
    'cog-icon': CogIcon,
    'account-voice-icon': AccountVoiceIcon,
    'gesture-tap-icon': GestureTapIcon,
    'grid-icon': GridIcon,
    'alert-icon': AlertIcon,
    'limited-icon': LimitedIcon,
    'waiting-room-icon': WaitingRoomIcon,
    'check-icon': CheckIcon,
    'moderator-key-icon': ModeratorKeyIcon,
    'lock-outline-icon': LockOutlineIcon,
    'raise-hand-icon': RaiseHandIcon,
    'become-speaker-icon': BecomeSpeakerIcon
  },
  props: {
    selectedVideoLayout: {
      type: Number,
      default: 0
    }
  },
  setup(props, context) {
    const selectedShareOption = ref('window')
    const mic = ref("")
    const speaker = ref(false)
    const miceControls = ref(true)
    const cameraControls = ref(true)
    const camera = ref("")
    const desktopControls = ref(true)
    const desktop = ref(false)
    const videolayoutControls = ref(true)
    const moderatorIds = ref([])

    const isMeetingLocked = computed(() => {
      return context.parent.meetingInfo ? context.parent.meetingInfo.isLocked : false
    })

    const canSeeAudioVideoCtrls = computed(() => {
      if (!context.parent.meetingInfo.webinar) {
        return true
      } else {
        let user = context.parent.$refs.sideBar.$refs.sidePart.participants.find(user => !!user.isSelf)
        const canSee = (user && user.rh && user.rh.substring(0,1) == "2") ? true : false
        if (canSee == false) {
          if (camera.value = true)
            camera.value = false
          if (mic.value = true)
            mic.value = false
        }
        return canSee
      }
    })

    const isRaisedHand = computed(() => {
      let user = context.parent.$refs.sideBar.$refs.sidePart.participants.find(user => !!user.isSelf);
      return user && user.rh && user.rh.substring(0,1) == "1"
    })

    function setMeetingOptions(meetingOptions) {
      console.log("meetingOptions", meetingOptions)
      speaker.value = true
      if (meetingOptions.options.includes('shareDesktop=0') && desktopControls.value) {
        desktopControls.value = false
      }
      if (meetingOptions.options.includes('audio=0')) {
        if (context.parent.localAudioStream.length > 0) {
          mic.value = false
        }
      } else if (meetingOptions.options.includes('audio=4')) {
        if (context.parent.localAudioStream.length > 0) {
          mic.value = true
        }

      } else if (meetingOptions.options.includes('audio=3')) {
        if (context.parent.localAudioStream.length > 0) {
          mic.value = false
        }
      }
      if (!meetingOptions.options.includes('audio=4') && !meetingOptions.options.includes('audio=3') && !meetingOptions.options.includes('audio=0')) {
        if (context.parent.localAudioStream.length > 0) {
          mic.value = context.parent.$refs.configDevices.miceOn
        }
      }
      if (meetingOptions.options.includes('video=0')) {
        if (context.parent.localVideoStreams.length > 0) {
          camera.value = false
        }

      } else if (meetingOptions.options.includes('video=4')) {
        if (context.parent.localVideoStreams.length > 0) {
          camera.value = true
        }
      } else if (meetingOptions.options.includes('video=3')) {
        if (context.parent.localVideoStreams.length > 0) {
          camera.value = false
        }
      }
      if (!meetingOptions.options.includes('video=4') && !meetingOptions.options.includes('video=3') && !meetingOptions.options.includes('video=0')) {
        if (context.parent.localVideoStreams.length > 0) {
          camera.value = context.parent.$refs.configDevices.cameraOn
        }
      }

      //if (!meetingOptions.options.includes('chat=12')) {
      context.parent.$refs.sideBar.$refs.sideChat.selectedChat = "public"
      //}
    }

    function changeVideoLayoutOption (id) {
      var layouts = ['gridVideo', 'click', 'speaker', 'stop', 'limited', 'gridAll'];
      gaEvent({'ACTION': 'action', 'video_layout': layouts[id]});
      if (id !== context.parent.videoMode) {
        if (id === 3) {
          // dont send video
          receiveVideo('video-off')
        } else if (id === 4) {
          receiveVideo('video-limited')
        } else {
          // send video
          receiveVideo('video-on')
        }
        context.parent.videoMode = id
      }
      context.refs.layoutRef.$emit('close')
    }

    function getDestination(){
      if (context.root.$route.query.meetingID) {
        if (context.root.$route.query.pin) {
          return (context.root.$route.query.meetingID + context.root.$route.query.pin)
        } else {
          return context.root.$route.query.meetingID
        }
      } else if (context.parent.meetingData.meetingID) {
        if (context.parent.meetingData.pin) {
          return (context.parent.meetingData.meetingID + context.parent.meetingData.pin)
        } else {
          return context.parent.meetingData.meetingID
        }
      } else {
        return
      }
    }

    function receiveVideo (value) {
      let eventHandlers = {
        'succeeded': E => console.log('send message video mode succeded!'),
        'failed': E => console.log('send message video mode failed!')
      }

      let options = {
        'eventHandlers': eventHandlers,
        'extraHeaders': ['Vibe-VideoMode: ' + value]
      }

      let destination = getDestination();
      context.parent.webrtcPhone.sendMessage(destination, '', options)
    }

    function sendControlUpdate(value, type) {
      let isAudio = type === 'mic';
      let output = `${isAudio}/${!value}/${context.parent.meetingData.name}`;
      let options = {
        'extraHeaders': ['Vibe-mute: ' + output]
      }
      let destination = getDestination();
      context.parent.webrtcPhone.sendMessage(destination, '', options)
    }

    function handleModerator(response) {
      var object = response.split('/')
      object = {'isAudio': object[0], 'isMute': object[1]}
      object.isAudio === "true" ? mic.value = !object.isMute : camera.value = !object.isMute;
      var string = object.isAudio === "true" ? 'microphone' : 'camera';
      context.root.$notify.info('A Moderator has turned off your ' + string + '.')
    }

    function broadcast () {
      cameraControls.value = false
      miceControls.value = false
      desktopControls.value = false
    }

    function onDesktopChange () {
      desktop.value = !desktop.value
      if (desktop.value) {
        gaEvent({'ACTION': 'action', 'video_action': 'deskOn'});
        context.parent.$refs.shareDesktop.shareDesktop()
      } else {
        gaEvent({'ACTION': 'action', 'video_action': 'deskOff'});
        context.parent.$refs.shareDesktop.stopSharing()
      }
    }

    function toggleText (check, string, verbFrom = 'CONFIGURE.STOP',verbTo='CONFIGURE.START') {
      return (context.root.$t((check ? verbFrom : verbTo) + `${string}`))
    }



    function toggleVideoList() {
      if (context.parent.videoDevices.length > 1) {
        context.refs.videoOptions.style.display = 'block'
      }
    }

    function toggleAudioList() {
      if (context.parent.audioDevices.length > 1) {
        context.refs.audioOptions.style.display = 'block'
      }
    }

    function launchSettings(pane){
      if (pane === 'audio') {
        gaEvent({'ACTION': 'action', 'video_more': 'aSettings'});
      } else if (pane === 'video') {
        gaEvent({'ACTION': 'action', 'video_more': 'vSettings'});
      }
      context.parent.$refs.settings.currentPane = pane
      context.parent.$refs.settings.init()
      this.$bvModal.show('modal-settings')
      context.refs.meetingSettings.$emit('close')
    }

    function launchLeave(){
      context.parent.$refs.leaveMeeting.init()
      if (!context.parent.$refs.leaveMeeting.settings.includes('confirmLeave'))
        exitMeeting()
      else
        this.$bvModal.show('modal-leave')
    }

    function launchMakeModerator(){
      this.$bvModal.show('modal-make-moderator')
      context.refs.meetingSettings.$emit('close')
    }

    function toggleSystemMessages(){
      gaEvent({'ACTION': 'action', 'video_more': 'sysMessage'});
      context.parent.$refs.sideBar.toggleBar('system')
      context.refs.meetingSettings.$emit('close')
    }

    function updateSelfPart(value, type) {
      let isAudio = type === 'mic';
      let user = context.parent.$refs.sideBar.$refs.sidePart.participants.find(user => !!user.isSelf);
      if(user) {
        const index = context.parent.$refs.sideBar.$refs.sidePart.participants.indexOf(user)
        if (isAudio) {
          user.isMuted = !value;
        } else {
          user.videoOff = !value;
        }
        context.parent.$refs.sideBar.$refs.sidePart.participants.splice(index, 1, user)  // this is literally a vue caveat
      }
    }

    function closeToolTip(){
      context.root.$emit('bv::hide::tooltip')
    }

    function enableMike(value) {
      updateSelfPart(value,'mic')
      if (context.parent.versionOfIos().status == true && context.parent.versionOfIos().version == 14) {
        context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = value
      } else {
        if (value) {
          gaEvent({'ACTION': 'action', 'video_action': 'micOn'});
          context.parent.changeDevice(context.parent.actualAudioDevice, 'audio')
        } else {
          gaEvent({'ACTION': 'action', 'video_action': 'micOff'});
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = value
        }
      }
      sendControlUpdate(value, 'mic')
    }

    function enableCamera(value) {
      updateSelfPart(value,'camera')
      if (value) {
        gaEvent({'ACTION': 'action', 'video_action': 'camOn'});
        context.parent.changeDevice(context.parent.actualVideoDevice, 'video')
      } else {
        gaEvent({'ACTION': 'action', 'video_action': 'camOff'});
        context.parent.sendStillImage()
      }
      if (context.parent.maxBw > 0) {
        context.parent.setMaxBw(context.parent.maxBw)
      }
      if(!desktop.value) {
        sendControlUpdate(value, 'camera')
      }
    }

    function assignLeave() {
      if (moderatorIds.value.length > 0) {
        moderatorIds.value.forEach( id => context.parent.makeModerator(id) )
        setTimeout(() => context.parent.exitMeeting(), 1000)
      }
    }

    function assignModerator(id) {
      let index = moderatorIds.value.findIndex(e => e == id)
      index >= 0 ? moderatorIds.value.splice(index, 1) : moderatorIds.value.push(id)
    }

    function canSeeAssignLeaveMenu() {
      if (context.parent.$refs.sideBar.$refs.sidePart.amImoderator) {
        if (context.parent.meetingInfo.waitingRoom == true) {
          if (context.parent.$refs.sideBar.$refs.sidePart.inMeeting.length > 1 &&
            context.parent.$refs.sideBar.$refs.sidePart.inMeeting.findIndex(p => {return (p.presence && p.presence.isModerator == true && p.isSelf != true)}) < 0)
            return true
        } else if (context.parent.meetingInfo.webinar == true){
          return true
        }
      }
      return false
    }

    function muteUsersAudio() {
      console.log("mute users audio")
      gaEvent({'ACTION': 'action', 'video_more': 'mute'});
      context.parent.$refs.sideBar.$refs.sidePart.inMeeting.forEach( person => {
        if (!person.isMuted)
          context.parent.blockUserMedia(person.id, true, false)
      })
      context.refs.meetingSettings.$emit('close')
    }

    function muteUsersVideo() {
      console.log("mute users video")
      gaEvent({'ACTION': 'action', 'video_more': 'mute'});
      context.parent.$refs.sideBar.$refs.sidePart.inMeeting.forEach( person => {
        if (!person.videoOff)
          context.parent.blockUserMedia(person.id, false, true)
      })
      context.refs.meetingSettings.$emit('close')
    }

    function toogleLock() {
      gaEvent({'ACTION': 'action', 'video_more': 'lock'});
      if (!isMeetingLocked.value) {
        this.$bvModal.show('modal-lock-notify')
      } else {
         context.parent.lockMeeting(false)
      }
      context.refs.meetingSettings.$emit('close')
    }

    function exitMeeting() {
      if (canSeeAssignLeaveMenu() == false)
        context.parent.exitMeeting()
    }

    function launchRaiseHand(value) {
      value ? this.$bvModal.show('modal-raise-hand') : context.parent.handCommand(value, null, "")
    }

    function toogleWebinar(value) {
      context.parent.webrtcMeta.send(JSON.stringify({
        d_type: "WSMeetingModify",
        webinarStarted: value ? 1 : 0
      }))
      if (value == false) {
        context.parent.$refs.sideBar.$refs.sidePart.participantList.forEach( person => {
           context.parent.webrtcMeta.send(JSON.stringify({
            d_type: "WSKickUser",
            kickUserID: person.id
          }))
        })
      }
    }

    watch(mic, (value, oldValue) => {
      enableMike(value)
    })

    watch(speaker, (value, oldValue) => {
      if (oldValue != undefined) {
        context.parent.remoteAudioStream.getAudioTracks()[0].enabled = value
      }
    })

    watch(desktop, (value, oldValue) => {
      if (oldValue != undefined) {

        if(!camera.value) {
          sendControlUpdate(value, 'camera')
        }
      }
    })

    watch(camera, (value, oldValue) => {
      console.log("watch camera: " + value)
      enableCamera(value)
    })
    return {
      selectedShareOption,
      changeVideoLayoutOption,
      mic,
      speaker,
      miceControls,
      cameraControls,
      camera,
      desktopControls,
      desktop,
      broadcast,
      onDesktopChange,
      videolayoutControls,
      setMeetingOptions,
      toggleVideoList,
      toggleAudioList,
      enableMike,
      enableCamera,
      sendControlUpdate,
      toggleText,
      launchSettings,
      closeToolTip,
      handleModerator,
      toggleSystemMessages,
      moderatorIds,
      assignLeave,
      assignModerator,
      canSeeAssignLeaveMenu,
      canSeeAudioVideoCtrls,
      isRaisedHand,
      launchRaiseHand,
      muteUsersAudio,
      muteUsersVideo,
      toogleLock,
      launchMakeModerator,
      launchLeave,
      isMeetingLocked,
      toogleWebinar,
      exitMeeting
    }
  }
}
</script>
<style>
  .control-is-mobile:hover span.material-design-icon { background-color: transparent !important; }

  .quit-title {
    font-weight: 600;
    font-size: 16px;
    color:#FFFFFF;
  }
  .quit-title:hover {
    cursor: default;
    background:#4a4a4a !important;
  }
  .quit-leave {
    border-radius: 5px;
    font-weight: 600;
    color: #E72113;
    background: #F8F8F8;
  }
  .quit-leave:hover {
    background: #F8F8F8 !important;
    opacity: 0.8;
  }
  .end-webinar {
    margin-top: 50px;
    border-radius: 5px;
    font-weight: 600;
    color: #F8F8F8;
    background: #E72113;
  }
  .end-webinar:hover {
    background: #E72113 !important;
    opacity: 0.8;
  }
</style>



// WEBPACK FOOTER //
// src/components/MeetingControls.vue